/**
 * Configuração central do menu lateral e breadcrumbs.
 *
 * Fonte única de verdade para hierarquia:
 * região > menu pai > menu filho > ... > item final
 */

export type MenuRegion = 'Dashboards' | 'Acesso rápido' | 'Produtos' | 'Ferramentas';

export interface MenuLeafConfig {
  path: string;
  label: string;
}

export interface MenuGroupConfig {
  key: string;
  label: string;
  children: Array<MenuGroupConfig | MenuLeafConfig>;
}

export interface MenuRegionConfig {
  region: MenuRegion;
  items: Array<MenuLeafConfig | MenuGroupConfig>;
}

const isMenuGroup = (item: MenuLeafConfig | MenuGroupConfig): item is MenuGroupConfig =>
  'children' in item;

/**
 * Estrutura do menu lateral.
 */
export const menuRegions: MenuRegionConfig[] = [
  {
    region: 'Dashboards',
    items: [
      { path: '/dashboard/status', label: 'Dashboard Status' },
      { path: '/dashboard/api-consulta', label: 'Dashboard API Consulta' },
    ],
  },
  {
    region: 'Acesso rápido',
    items: [
      { path: '/buscar-fonte', label: 'Buscar fonte' },
      { path: '/push/buscar-id', label: 'Buscar ID' },
      { path: '/api-callback', label: 'Ver Callbacks' },
    ],
  },
  {
    region: 'Produtos',
    items: [
      {
        key: 'api-consulta',
        label: 'API Consulta',
        children: [
          {
            key: 'autorequest',
            label: 'Autorequest',
            children: [
              { path: '/auto-request', label: 'Novo autorequest' },
              { path: '/auto-request/buscar', label: 'Buscar por autorequest' },
            ],
          },
          {
            key: 'capturaweb',
            label: 'Consulta',
            children: [
              { path: '/request/create', label: 'Nova captura' },
              { path: '/request/create-many', label: 'Buscar várias capturas' },
            ],
          },
        ],
      },
      {
        key: 'push',
        label: 'API Push',
        children: [
          { path: '/push', label: 'Novo push' },
          { path: '/push/visualizar', label: 'Visualizar vários' },
          { path: '/push/reprocessar', label: 'Reprocessar' },
          { path: '/push/reenviar-callbacks', label: 'Reenviar callback' },
          { path: '/reports', label: 'Análise de ID' },
          { path: '/push/atrasados', label: 'Atrasados' },
        ],
      },
      {
        key: 'dt',
        label: 'API DT',
        children: [
          { path: '/datateam/novo', label: 'Novo DT' },
          { path: '/datateam/visualizar', label: 'Visualizar vários' },
          { path: '/datateam/reprocessar', label: 'Reprocessar' },
        ],
      },
    ],
  },
  {
    region: 'Ferramentas',
    items: [
      { path: '/abrangencia', label: 'Abrangência' },
      { path: '/status/ocorrencias', label: 'Status Ocorrências' },
      { path: '/integrations/solucionare', label: 'Integrações Solucionare' },
      { path: '/consumos', label: 'Captchas / Proxies' },
    ],
  },
];

/**
 * Rotas auxiliares fora do menu principal, mas com breadcrumb derivado.
 */
const auxiliaryRouteBreadcrumbs: Record<string, string[]> = {
  '/request': ['Produtos', 'API Consulta', 'Consulta', 'Visualizar requisições'],
  '/datateam': ['Produtos', 'API DT'],
};

interface DynamicBreadcrumbRule {
  pattern: RegExp;
  buildTrail: (pathname: string) => string[] | null;
}

const dynamicBreadcrumbRules: DynamicBreadcrumbRule[] = [
  {
    pattern: /\/monitoring$/,
    buildTrail: (pathname) => {
      const basePath = pathname.replace(/\/monitoring$/, '');
      const baseTrail = findStaticMenuBreadcrumbTrail(basePath);
      return baseTrail ? [...baseTrail, 'Monitoramento'] : null;
    },
  },
  {
    pattern: /^\/buscar-fonte\/[^/]+$/,
    buildTrail: () => ['Acesso rápido', 'Buscar fonte', 'Resultado da busca'],
  },
  {
    pattern: /^\/push-details\/[^/]+$/,
    buildTrail: () => ['Produtos', 'API Push', 'Visualizar vários', 'Detalhes do push'],
  },
  {
    pattern: /^\/autorequest-details\/[^/]+$/,
    buildTrail: () => ['Produtos', 'API Consulta', 'Autorequest', 'Buscar por autorequest', 'Detalhes do autorequest'],
  },
];

const findLeafTrail = (
  items: Array<MenuLeafConfig | MenuGroupConfig>,
  path: string,
  trail: string[],
): string[] | null => {
  for (const item of items) {
    if (isMenuGroup(item)) {
      const groupTrail = findLeafTrail(item.children, path, [...trail, item.label]);
      if (groupTrail) {
        return groupTrail;
      }
      continue;
    }

    if (item.path === path) {
      return [...trail, item.label];
    }
  }

  return null;
};

/**
 * Busca a trilha estática do menu para um path exato.
 */
export const findStaticMenuBreadcrumbTrail = (path: string): string[] | null => {
  const normalizedPath = path.split('?')[0];

  for (const regionConfig of menuRegions) {
    const trail = findLeafTrail(regionConfig.items, normalizedPath, [regionConfig.region]);
    if (trail) {
      return trail;
    }
  }

  return auxiliaryRouteBreadcrumbs[normalizedPath] ?? null;
};

export interface MenuBreadcrumbItem {
  title: string;
  path?: string;
}

/**
 * Monta breadcrumb no formato região > pai > filho > ... > item final.
 */
export const buildMenuBreadcrumb = (pathname: string): MenuBreadcrumbItem[] => {
  const normalizedPath = pathname.split('?')[0];

  for (const rule of dynamicBreadcrumbRules) {
    if (rule.pattern.test(normalizedPath)) {
      const trail = rule.buildTrail(normalizedPath);
      if (trail) {
        return trailToBreadcrumbItems(trail, normalizedPath);
      }
    }
  }

  const staticTrail = findStaticMenuBreadcrumbTrail(normalizedPath);
  if (staticTrail) {
    return trailToBreadcrumbItems(staticTrail, normalizedPath);
  }

  return [{ title: normalizedPath }];
};

const trailToBreadcrumbItems = (trail: string[], currentPath: string): MenuBreadcrumbItem[] => {
  const isMonitoring = currentPath.endsWith('/monitoring');
  const basePath = isMonitoring ? currentPath.replace(/\/monitoring$/, '') : currentPath;

  return trail.map((title, index) => {
    const isLast = index === trail.length - 1;

    if (isLast) {
      return { title };
    }

    if (isMonitoring && index === trail.length - 2) {
      return { title, path: basePath };
    }

    const partialPath = findMenuLeafPathByTrail(trail.slice(0, index + 1));
    return partialPath ? { title, path: partialPath } : { title };
  });
};

const findMenuLeafPathByTrail = (trail: string[]): string | undefined => {
  const [regionLabel, ...menuLabels] = trail;
  const regionConfig = menuRegions.find((region) => region.region === regionLabel);

  if (!regionConfig || menuLabels.length === 0) {
    return undefined;
  }

  return findLeafPathInItems(regionConfig.items, menuLabels);
};

const findLeafPathInItems = (
  items: Array<MenuLeafConfig | MenuGroupConfig>,
  labels: string[],
): string | undefined => {
  if (labels.length === 0) {
    return undefined;
  }

  const [currentLabel, ...remaining] = labels;

  for (const item of items) {
    if (isMenuGroup(item)) {
      if (item.label !== currentLabel) {
        continue;
      }

      const nestedPath = findLeafPathInItems(item.children, remaining);
      if (nestedPath) {
        return nestedPath;
      }
      continue;
    }

    if (item.label === currentLabel && remaining.length === 0) {
      return item.path;
    }
  }

  return undefined;
};

/**
 * Retorna as chaves de submenu que devem permanecer abertas para a rota atual.
 */
export const getMenuOpenKeysForPath = (pathname: string): string[] => {
  const normalizedPath = pathname.split('?')[0];

  if (normalizedPath.startsWith('/auto-request') || normalizedPath.startsWith('/autorequest-details')) {
    return ['api-consulta', 'autorequest'];
  }

  if (normalizedPath.startsWith('/request')) {
    return ['api-consulta', 'capturaweb'];
  }

  if (
    normalizedPath.startsWith('/push')
    || normalizedPath.startsWith('/reports')
    || normalizedPath.startsWith('/push-details')
  ) {
    if (normalizedPath === '/push/buscar-id') {
      return [];
    }
    return ['push'];
  }

  if (normalizedPath.startsWith('/datateam')) {
    return ['dt'];
  }

  return [];
};
